import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { login, requestEmailCode } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import {
  Loader2,
  Eye,
  EyeOff,
  LogIn,
  Mail,
  KeyRound,
  AtSign,
  Lock,
  UserPlus,
  ArrowRight,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import LoginLanguageSwitcher from '../components/auth/LoginLanguageSwitcher';
import { authErrorMessage, safeAuthNext } from '../lib/authFlow';
import SocialAuthButtons from '../components/auth/SocialAuthButtons';
import './login-page.css';
import EmailCodeChallenge from '../components/auth/EmailCodeChallenge';
import { readCodeFlow, saveCodeFlow, clearCodeFlow, codeError } from '../lib/emailCodeFlow';

// Keep legacy mode=magic entry points; ordinary login uses password.
function initialLoginMode(location) {
  const requested = new URLSearchParams(location?.search || '').get('mode') || location?.state?.mode;
  if (requested === 'password' || requested === 'magic') return requested;
  return new URLSearchParams(location?.search || '').get('next') === '/onboarding' ? 'magic' : 'password';
}

const CARD_CLASS = 'tn-auth-card';
const PRIMARY_BTN_CLASS = 'tn-auth-primary';
const SECONDARY_BTN_CLASS = 'tn-auth-secondary';

function IconInput({ icon: Icon, ...props }) {
  return (
    <div className="tn-auth-input-wrap">
      <Icon
        className="tn-auth-input-icon"
        aria-hidden="true"
      />
      <Input
        {...props}
        className={`tn-auth-input ${props.className || ''}`}
      />
    </div>
  );
}

function OrDivider({ label }) {
  return <div className="tn-auth-divider" aria-hidden="true"><span>{label}</span></div>;
}

function Brand() {
  return <Link to="/welcome" className="tn-auth-brand" data-testid="login-back-home" aria-label="Trainnova">
    <span className="tn-auth-brand-mark" aria-hidden="true"><img src="/trainnova-logo-cropped.png" alt="" width="1213" height="205" /></span>
    <span>Trainnova<span className="tn-auth-brand-dot">.</span></span>
  </Link>;
}

function ProductStory() {
  const { t } = useTranslation('landing');
  return <aside className="tn-auth-story" aria-labelledby="tn-auth-story-title">
    <div className="tn-auth-story-copy">
      <h2 id="tn-auth-story-title">{t('web.hero.line1')}<br />{t('web.hero.line2')}</h2>
      <p>{t('web.hero.copy')}</p>
    </div>
    <figure className="tn-auth-product">
      <div className="tn-auth-laptop">
        <div className="tn-auth-screen"><img src="/landing-shots/new-story/story-training-plan.webp" alt={t('web.images.plan')} width="1655" height="1080" loading="lazy" decoding="async" /></div>
        <div className="tn-auth-laptop-base" aria-hidden="true" />
      </div>
      <div className="tn-auth-phone"><img src="/landing-shots/new-story/story-player-preparation.webp" alt={t('web.v2.preparation_alt')} width="390" height="844" loading="lazy" decoding="async" /></div>
      <figcaption>{t('web.hero.devices')}</figcaption>
    </figure>
  </aside>;
}

function AuthShell({ children }) {
  return <div className="tn-auth">
    <header className="tn-auth-header"><Brand /><LoginLanguageSwitcher className="tn-auth-language" /></header>
    <div className="tn-auth-layout">
      <ProductStory />
      <main className="tn-auth-main">
        <div className="tn-auth-form-area">{children}</div>
        <footer className="tn-auth-footer">© {new Date().getFullYear()} Trainnova</footer>
      </main>
    </div>
  </div>;
}

function GettingStarted({ onStartTeam, onJoinTeam, startingTeam }) {
  const { t, i18n } = useTranslation(['auth', 'landing']);
  const sv = (i18n.resolvedLanguage || i18n.language || 'sv').startsWith('sv');
  return (
    <div className="tn-auth-getting-started" data-testid="login-getting-started">
      <p className="tn-auth-new-user">{t('auth:login.new_user_title')}</p>
      <div className="mt-1 flex flex-wrap items-center justify-center gap-x-5">
        <button type="button" onClick={onStartTeam} aria-current={startingTeam ? 'step' : undefined}
          className="tn-auth-start-link"
          data-testid="login-start-team-btn">
          <UserPlus className="h-4 w-4" aria-hidden="true" />{t('landing:header.apply')}
        </button>
        <button type="button" onClick={onJoinTeam}
          className="tn-auth-join-link"
          data-testid="login-join-team-btn">
          <Users className="h-4 w-4" aria-hidden="true" />{sv ? 'Jag har en lagkod' : 'I have a team code'}
        </button>
      </div>
    </div>
  );
}

export default function Login() {
  const { t, i18n } = useTranslation(['auth', 'common', 'landing']);
  const location = useLocation();
  const [email, setEmail] = useState(typeof location.state?.email === 'string' ? location.state.email : '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const sv = (i18n.resolvedLanguage || i18n.language || 'sv').startsWith('sv');
  const [loading, setLoading] = useState(false);
  // Password is the default; email codes are an explicit alternative.
  // These are ALTERNATIVES, not sequential steps — no step indicator is shown.
  const [mode, setMode] = useState(() => initialLoginMode(location));
  const [flow, setFlow] = useState(() => new URLSearchParams(location.search).get('mode') === 'password' ? null : readCodeFlow('login'));
  const setMagicSent = () => { clearCodeFlow('login'); setFlow(null); };
  const [errorMessage, setErrorMessage] = useState('');
  const inFlight = React.useRef(false);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    setMode(initialLoginMode(location));
  }, [location]);

  // Preserve the identifier the user actually typed (email OR username) so the
  // confirmation shows exactly that — never the backend-resolved delivery email.
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  // Post-login redirect target. Accepts `?next=/path` — only same-origin
  // internal paths are honoured (leading "/" required, no absolute URLs) so
  // welcome-email deep-links cannot be abused for open redirects.
  const nextTarget = React.useMemo(() => {
    try {
      const raw = new URLSearchParams(location.search).get('next');
      return safeAuthNext(raw);
    } catch {
      return null;
    }
  }, [location.search]);

  const startingTeam = nextTarget === '/onboarding';
  const creatingAccount = startingTeam && mode === 'magic';

  const handleMagicLink = async (e) => {
    e.preventDefault();
    const identifier = email.trim();
    if (inFlight.current || !identifier) return;

    inFlight.current = true;
    setLoading(true);
    setErrorMessage('');

    try {
      // Name is never collected here; the backend fills it in later via the
      // canonical onboarding when a genuinely new account needs completing.
      const result = await requestEmailCode(identifier, 'login');
      if (!mounted.current) return;
      setFlow(saveCodeFlow(result.data, identifier, 'login'));
    } catch (error) {
      if (mounted.current) setErrorMessage(codeError(error, sv));
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    const identifier = email.trim();
    if (inFlight.current || !identifier || !password) return;

    inFlight.current = true;
    setLoading(true);
    setErrorMessage('');

    try {
      const res = await login(identifier, password);
      if (!mounted.current) return;

      await loginUser(res.data.access_token, res.data.user);
      if (localStorage.getItem('token') !== res.data.access_token) return;

      clearCodeFlow('login');
      toast.success(t('auth:login.welcome_toast'));
      navigate(nextTarget || '/', { replace: true });
    } catch (error) {
      if (mounted.current) setErrorMessage(authErrorMessage(error, i18n, 'login'));
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  const handleStartTeam = () => {
    setErrorMessage('');
    setMagicSent(false);
    setMode('magic');
    setPassword('');
    setShowPassword(false);
    navigate('/login?next=/onboarding', {
      replace: true,
      state: email ? { email } : undefined,
    });
  };

  const handleJoinTeam = () => {
    navigate('/join');
  };

  if (flow) {
    return <AuthShell><Card className={CARD_CLASS}><CardContent className="tn-auth-card-content pt-6">
      <EmailCodeChallenge flow={flow} onChange={setFlow}
        onBack={() => { setEmail(flow.identifier); setFlow(null); setMode('magic'); }}
        onComplete={async data => {
          await loginUser(data.access_token, data.user);
          if (localStorage.getItem('token') !== data.access_token) throw new Error('Session not saved');
          navigate(nextTarget || '/', { replace: true });
        }} />
    </CardContent></Card></AuthShell>;
  }

  return (
    <AuthShell>
      {startingTeam && (
        <div
          className="tn-auth-team-context"
          data-testid="start-team-context"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#C6A85E]/10">
            <UserPlus className="h-4 w-4 text-[rgb(var(--tn-text-c6a85e))]" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[rgb(var(--tn-text-ffffff))]">{t('landing:header.apply')}</p>
            <p className="mt-0.5 text-xs text-[rgb(var(--tn-text-737373))]">{t('landing:web.hero.price')}</p>
          </div>
        </div>
      )}

      <Card className={CARD_CLASS} data-testid="login-card">
        <CardHeader className="tn-auth-card-heading">
          <div className="tn-auth-heading-icon">
            {mode === 'magic'
              ? <Mail className="h-[18px] w-[18px] text-[rgb(var(--tn-text-c6a85e))]" />
              : <Lock className="h-[18px] w-[18px] text-[rgb(var(--tn-text-c6a85e))]" />}
          </div>

          <h1 className="tn-auth-title">
            {creatingAccount ? (sv ? 'Skapa ditt konto' : 'Create your account') : t('landing:header.login')}
          </h1>
          {creatingAccount && (
            <CardDescription className="tn-auth-description">
              {sv ? 'Skapa först ditt konto. Därefter lägger du till ditt lag.' : 'Create your account first. Then add your team.'}
            </CardDescription>
          )}


        </CardHeader>

        <CardContent className="tn-auth-card-content">
          {errorMessage && (
            <p
              role="alert"
              className="tn-auth-error"
              data-testid="login-error"
            >
              {errorMessage}
            </p>
          )}

          {mode === 'magic' ? (
            <form onSubmit={handleMagicLink} className="space-y-4" data-testid="magic-link-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[13px] text-[rgb(var(--tn-text-a3a3a3))]">
                  {creatingAccount ? (sv ? 'Din e-postadress' : 'Your email address') : t('auth:login.email_label_password')}
                </Label>

                <IconInput
                  icon={AtSign}
                  id="email"
                  type={creatingAccount ? 'email' : 'text'}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete={creatingAccount ? 'email' : 'username'}
                  placeholder={creatingAccount ? (sv ? 'namn@exempel.se' : 'name@example.com') : t('auth:login.email_placeholder_password')}
                  value={email}
                  disabled={loading}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />

                <p className="px-0.5 text-[11.5px] leading-5 text-[rgb(var(--tn-text-525252))]">
                  {sv ? 'Vi skickar en sexsiffrig kod som du anger här i Trainnova.' : 'We will email a six-digit code for you to enter here in Trainnova.'}
                </p>
              </div>

              <Button
                type="submit"
                className={PRIMARY_BTN_CLASS}
                disabled={loading || !email.trim()}
                data-testid="magic-link-submit-btn"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                {sv ? 'Skicka mejlkod' : 'Send email code'}
              </Button>

              {creatingAccount && <div className="tn-auth-social"><SocialAuthButtons next={nextTarget || '/'} disabled={loading} label={sv ? 'Fortsätt med Google' : 'Continue with Google'} /></div>}
              <OrDivider label={t('auth:login.or_divider')} />

              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setMode('password');
                  setErrorMessage('');
                }}
                className={SECONDARY_BTN_CLASS}
                data-testid="switch-to-password-btn"
              >
                <KeyRound className="h-3.5 w-3.5" />
                {creatingAccount ? (sv ? 'Har du redan ett konto? Logga in' : 'Already have an account? Sign in') : t('auth:login.switch_to_password')}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[13px] text-[rgb(var(--tn-text-a3a3a3))]">
                  {t('auth:login.email_label_password')}
                </Label>

                <IconInput
                  icon={AtSign}
                  id="email"
                  type="text"
                  placeholder={t('auth:login.email_placeholder_password')}
                  value={email}
                  disabled={loading}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="password" className="text-[13px] text-[rgb(var(--tn-text-a3a3a3))]">
                    {t('auth:login.password_label')}
                  </Label>

                  <Link
                    to="/forgot-password"
                    state={{ email: email.trim() }}
                    className="tn-auth-forgot"
                    data-testid="forgot-password-link"
                  >
                    {t('auth:login.forgot_password')}
                  </Link>
                </div>

                <div className="relative">
                <IconInput
                  icon={Lock}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="pr-12"
                  placeholder={t('auth:login.password_placeholder')}
                  value={password}
                  disabled={loading}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                  <button type="button" disabled={loading}
                    onClick={() => setShowPassword(value => !value)}
                    aria-label={sv ? (showPassword ? 'Dölj lösenord' : 'Visa lösenord') : (showPassword ? 'Hide password' : 'Show password')}
                    aria-pressed={showPassword}
                    className="tn-auth-password-toggle">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className={PRIMARY_BTN_CLASS}
                disabled={loading || !email.trim() || !password}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LogIn className="mr-2 h-4 w-4" />
                )}
                {t('auth:login.submit_password')}
              </Button>

              <OrDivider label={t('auth:login.or_divider')} />
              <div className="tn-auth-social tn-auth-alternatives">
                <SocialAuthButtons next={nextTarget || '/'} disabled={loading} label="Google" compact />
                <button type="button" disabled={loading}
                  onClick={() => { setMode('magic'); setPassword(''); setShowPassword(false); setErrorMessage(''); }}
                  className="tn-auth-email-option"
                  data-testid="switch-to-magic-btn">
                  <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {sv ? 'Mejlkod' : 'Email code'}
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {!startingTeam && <GettingStarted
        onStartTeam={handleStartTeam}
        onJoinTeam={handleJoinTeam}
        startingTeam={startingTeam}
      />}
    </AuthShell>
  );
}
